import React, { useState, useEffect } from 'react';
import Sidebaradmin from "../Sidebar";
import Navbaradmin from "../navbar";
import { Spinner } from 'react-bootstrap';  // Importing a spinner for loading feedback
import ExistingDynamicPricingRules from './ExistingDynamicPricingRules'; // Importing the new component

const DynamicPricingAdminPage = () => {
    const [products, setProducts] = useState([]);
    const [dynamicPricingList, setDynamicPricingList] = useState([]); // To store all dynamic pricing
    const [pricingData, setPricingData] = useState({
        productId: '',
        basePrice: '',
        salePrice: '',
        condition: '',
        startDate: '',
        endDate: '',
        freeMonths: 0,  // New field for free months
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(''); // To store error messages
    const [successMessage, setSuccessMessage] = useState(''); // To store success messages

    // Fetch products data when the component is mounted
    useEffect(() => {
        fetch('/api/products')
            .then(response => response.json())
            .then(data => setProducts(data))
            .catch(error => {
                console.log('Error fetching products:', error);
                setError('Failed to load products');
            });
    }, []); 

    // Fetch dynamic pricing data when the component is mounted
    useEffect(() => {
        fetch('/api/dynamic-pricing/products')
            .then(response => response.json())
            .then(data => {
                if (data && Array.isArray(data.products)) {
                    setDynamicPricingList(data.products);  // Use the 'products' array
                } else {
                    setError('Failed to load dynamic pricing: Products array is missing or malformed');
                }
            })
            .catch(error => {
                console.log('Error fetching dynamic pricing:', error);
                setError('Failed to load dynamic pricing');
            });
    }, []); 

    // Handle change in input fields and set basePrice if a product is selected
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'productId') {
            const selectedProduct = products.find(product => product._id === value);
            if (selectedProduct) {
                // Set the base price to the selected product's totalPrice
                setPricingData(prevData => ({
                    ...prevData,
                    productId: value,
                    basePrice: selectedProduct.totalPrice, // Assuming totalPrice exists on the product
                }));
            }
        } else {
            setPricingData(prevData => ({ ...prevData, [name]: value }));
        }
    };

    // Handle adding a new dynamic pricing rule
    const handleAddPricing = () => {
        if (!pricingData.productId || !pricingData.basePrice || !pricingData.salePrice || !pricingData.condition || !pricingData.startDate || !pricingData.endDate) {
            alert("All fields are required.");
            return;
        }
        setLoading(true);
        fetch('/api/dynamic-pricing', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pricingData),
        })
            .then(response => response.json())
            .then(data => {
                setSuccessMessage('Pricing rule added successfully');
                setDynamicPricingList(prevList => [...prevList, data.pricing]); // Add new pricing to the list
                setPricingData({
                    productId: '',
                    basePrice: '',
                    salePrice: '',
                    condition: '',
                    startDate: '',
                    endDate: '',
                    freeMonths: 0,  // Reset freeMonths
                });
            })
            .catch(error => {
                console.error(error);
                setError('Failed to add pricing rule');
            })
            .finally(() => setLoading(false));
    };

    // Handle updating an existing dynamic pricing rule
    const handleUpdatePricing = (id) => {
        if (!pricingData.basePrice || !pricingData.salePrice || !pricingData.condition || !pricingData.startDate || !pricingData.endDate) {
            alert("All fields are required.");
            return;
        }
        setLoading(true);
        fetch(`/api/dynamic-pricing/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pricingData),
        })
            .then(response => response.json())
            .then(data => {
                setSuccessMessage('Pricing rule updated successfully');
                setDynamicPricingList(prevList => prevList.map(item => item._id === id ? data.dynamicPricing : item)); // Update pricing in the list
            })
            .catch(error => {
                console.error(error);
                setError('Failed to update pricing rule');
            })
            .finally(() => setLoading(false));
    };

    // Handle deleting a dynamic pricing rule
    const handleDeletePricing = (id) => {
        setLoading(true);
        fetch(`/api/dynamic-pricing/${id}`, {
            method: 'DELETE',
        })
            .then(() => {
                setSuccessMessage('Pricing rule deleted successfully');
                setDynamicPricingList(prevList => prevList.filter(item => item._id !== id)); // Remove pricing from the list
            })
            .catch(error => {
                console.error(error);
                setError('Failed to delete pricing rule');
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <Sidebaradmin /> {/* Sidebar component */}
            
            <div className="flex-1 flex flex-col overflow-hidden bg-white shadow-lg rounded-lg mx-6 my-4">
                <Navbaradmin /> {/* Navbar component */}

                <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-700">Dynamic Pricing Management</h2>

                    {/* Error or Success Message */}
                    {error && (
                        <div className="text-red-600 bg-red-100 p-3 rounded mb-4">{error}</div>
                    )}
                    {successMessage && (
                        <div className="text-green-600 bg-green-100 p-3 rounded mb-4">{successMessage}</div>
                    )}

                    {/* Add or Update Pricing Form */}
                    <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
                        <h3 className="text-xl font-semibold text-gray-600">Add or Update Pricing Rule</h3>
                        <form onSubmit={e => e.preventDefault()}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Product:</label>
                                    <select
                                        name="productId"
                                        value={pricingData.productId}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Product</option>
                                        {products.map(product => (
                                            <option key={product._id} value={product._id}>
                                                {product.name}
                                            </option>
                                        ))} 
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Base Price:</label>
                                    <input
                                        type="number"
                                        name="basePrice"
                                        value={pricingData.basePrice}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={pricingData.basePrice ? `$${pricingData.basePrice}` : 'Base price will appear here'}
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Sale Price:</label>
                                    <input
                                        type="number"
                                        name="salePrice"
                                        value={pricingData.salePrice}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Condition:</label>
                                    <input
                                        type="text"
                                        name="condition"
                                        value={pricingData.condition}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Start Date:</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={pricingData.startDate}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">End Date:</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={pricingData.endDate}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Free Months Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Free Months:</label>
                                    <input
                                        type="number"
                                        name="freeMonths"
                                        value={pricingData.freeMonths}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="mt-4 text-center">
                                <button
                                    type="button"
                                    onClick={handleAddPricing}
                                    className="bg-blue-600 text-white px-6 py-2 rounded-md focus:outline-none"
                                >
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Add Pricing Rule'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Existing Dynamic Pricing Rules */}
                <ExistingDynamicPricingRules
                    dynamicPricingList={dynamicPricingList}
                    handleDeletePricing={handleDeletePricing}
                    handleUpdatePricing={handleUpdatePricing}
                />
            </div>
        </div>
    );
};

export default DynamicPricingAdminPage;
